import { chapterStatusLabels } from "@/types/story";
import type { SubscriptionTier } from "@/types/projects";
import {
  getBookExportCapabilitiesForTier,
  normalizeBookExportConfigForSource,
} from "@/lib/book-assembly/export-capabilities";
import { buildBookExportSummary } from "@/lib/book-assembly/export-manifest";
import {
  convertPlainTextToHtml,
  convertStoredHtmlToPlainText,
} from "@/lib/book-assembly/export-text";
import {
  type BookExportBookMetadata,
  createDefaultBookExportConfig,
  type BookExportConfig,
  type BookExportManifestSource,
  type BookExportProjectIdentity,
  type BookExportProjectOverrides,
} from "@/lib/book-assembly/export-contract";
import type {
  BookAssemblyAvailability,
  BookAssemblyDraft,
  BookAssemblySourceChapter,
  BookAssemblySourceSideStory,
  BookAssemblySourceView,
  MainChapterSelection,
  OrderedAssemblyItem,
  SideStoryPlacementChoice,
  SideStoryPlacementSuggestionSet,
} from "@/features/book-assembly/book-assembly.types";
import {
  createDefaultBookExportSettings,
  createIdleBookAssemblyRun,
} from "@/features/book-assembly/book-assembly.types";
import {
  collectIncludedSideStoryIds,
  createOrderedItemsFromSelections,
  inferBookExportModeFromSelections,
} from "@/lib/book-assembly/manuscript-compiler";

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function normalizeOptionalPath(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeOptionalText(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

function convertRichTextToSimpleHtml(value: unknown) {
  const content = typeof value === "object" && value && "content" in value
    ? ((value as { content?: Array<{ content?: Array<{ text?: string }> }> }).content ?? [])
    : [];

  const paragraphs = content
    .map((node) => {
      const text = (node.content ?? [])
        .map((segment) => segment.text ?? "")
        .join("")
        .trim();

      return text.length > 0 ? `<p>${escapeHtml(text)}</p>` : "";
    })
    .filter((paragraph) => paragraph.length > 0);

  return paragraphs.length > 0 ? paragraphs.join("") : "<p></p>";
}

function buildProjectIdentity(input: {
  authorName?: string | null;
  bookSeriesName?: string | null;
  bookSubtitle?: string | null;
  bookVolumeLabel?: string | null;
  coverImagePath?: string | null;
  projectId: string;
  projectTitle: string;
}) {
  return {
    author_name: input.authorName?.trim() || "Unknown Author",
    cover_image_path: normalizeOptionalPath(input.coverImagePath),
    id: input.projectId,
    series_name: input.bookSeriesName?.trim() || null,
    subtitle: input.bookSubtitle?.trim() || null,
    title: input.projectTitle,
    volume_label: input.bookVolumeLabel?.trim() || null,
  } satisfies BookExportProjectIdentity;
}

function buildBookMetadata(input: {
  authorNoteHtml?: string | null;
  dedicationHtml?: string | null;
  specialThanksHtml?: string | null;
}) {
  return {
    author_note_html: input.authorNoteHtml ?? null,
    dedication_html: input.dedicationHtml ?? null,
    special_thanks_html: input.specialThanksHtml ?? null,
  } satisfies BookExportBookMetadata;
}

export function buildBookAssemblyAvailability(input: {
  chapterCount: number;
  mainStoryMode: "chapters" | "continuous" | null;
  tier: SubscriptionTier;
}) {
  let availability: BookAssemblyAvailability;

  if (!input.mainStoryMode) {
    availability = {
      code: "no-main-story",
      eligible: false,
      reason: "bookAssembly.messages.noMainStory",
    };
  } else if (input.mainStoryMode === "continuous") {
    availability = {
      code: "continuous-main-story",
      eligible: false,
      reason: "bookAssembly.messages.continuousOnly",
    };
  } else if (input.chapterCount === 0) {
    availability = {
      code: "no-chapters",
      eligible: false,
      reason: "bookAssembly.messages.noChapters",
    };
  } else {
    availability = {
      code: "available",
      eligible: true,
      reason: null,
      tierLocked: false,
    };
  }

  return availability;
}

export function mapBookAssemblySource(input: {
  authorName?: string | null;
  authorNoteHtml?: string | null;
  bookSeriesName?: string | null;
  bookSubtitle?: string | null;
  bookVolumeLabel?: string | null;
  chapters: BookAssemblySourceChapter[];
  coverImagePath?: string | null;
  dedicationHtml?: string | null;
  mainStoryId: string | null;
  mainStoryMode: "chapters" | "continuous" | null;
  projectId: string;
  projectTitle: string;
  sideStories: BookAssemblySourceSideStory[];
  specialThanksHtml?: string | null;
  tier: SubscriptionTier;
}) {
  const capabilities = getBookExportCapabilitiesForTier(input.tier);

  return {
    availability: buildBookAssemblyAvailability({
      chapterCount: input.chapters.length,
      mainStoryMode: input.mainStoryMode,
      tier: input.tier,
    }),
    bookMetadata: buildBookMetadata({
      authorNoteHtml: input.authorNoteHtml,
      dedicationHtml: input.dedicationHtml,
      specialThanksHtml: input.specialThanksHtml,
    }),
    capabilities,
    chapters: input.chapters,
    exportIdentity: buildProjectIdentity({
      authorName: input.authorName,
      bookSeriesName: input.bookSeriesName,
      bookSubtitle: input.bookSubtitle,
      bookVolumeLabel: input.bookVolumeLabel,
      coverImagePath: input.coverImagePath,
      projectId: input.projectId,
      projectTitle: input.projectTitle,
    }),
    mainStoryId: input.mainStoryId,
    mainStoryMode: input.mainStoryMode,
    projectId: input.projectId,
    projectTitle: input.projectTitle,
    sideStories: input.sideStories,
    tier: input.tier,
  } satisfies BookAssemblySourceView;
}

export function createInitialBookAssemblyDraft(
  source: BookAssemblySourceView,
): BookAssemblyDraft {
  const selectedChapterIds = source.chapters.map((chapter) => chapter.id);
  const fallbackAnchorChapterId = source.chapters.find((chapter) => chapter.isClosing)?.id
    ?? source.chapters.at(-1)?.id
    ?? source.chapters[0]?.id
    ?? null;
  const sideStoryPlacements: SideStoryPlacementChoice[] = source.sideStories.map((story) => ({
    anchorChapterId: fallbackAnchorChapterId,
    included: false,
    placementKind: "after-chapter",
    storyId: story.id,
    summary: story.summary,
    title: story.title,
  }));
  const orderedItems = createOrderedItemsFromSelections({
    chapters: source.chapters,
    placements: sideStoryPlacements,
    selectedChapterIds,
    sideStories: source.sideStories,
  });
  const settings = createDefaultBookExportSettings(
    source.projectTitle,
    {
      authorName: source.exportIdentity?.author_name ?? "",
      authorNote: convertStoredHtmlToPlainText(source.bookMetadata.author_note_html),
      bookSubtitle: source.exportIdentity?.subtitle ?? "",
      bookTitle: source.exportIdentity?.title ?? source.projectTitle,
      dedication: convertStoredHtmlToPlainText(source.bookMetadata.dedication_html),
      seriesName: source.exportIdentity?.series_name ?? "",
      specialThanks: convertStoredHtmlToPlainText(source.bookMetadata.special_thanks_html),
      volumeLabel: source.exportIdentity?.volume_label ?? "",
    },
  );

  const draftBase = {
    orderedItems,
    projectId: source.projectId,
    run: createIdleBookAssemblyRun(),
    selectedChapterIds,
    settings,
    sideStoryPlacements,
    step: "chapter-selection" as const,
    suggestionSet: null,
  };

  return {
    ...draftBase,
    exportConfig: buildBookExportConfigFromDraft(source, draftBase),
    summary: buildBookExportSummaryFromDraft(source, draftBase),
  };
}

export function buildChapterSelections(
  source: BookAssemblySourceView,
  draft: BookAssemblyDraft,
): MainChapterSelection[] {
  const selectedIds = new Set(draft.selectedChapterIds);

  return source.chapters
    .slice()
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((chapter) => ({
      chapterId: chapter.id,
      chapterNumber: chapter.sortOrder + 1,
      included: selectedIds.has(chapter.id),
      isClosing: chapter.isClosing,
      isOpening: chapter.isOpening,
      locked: chapter.isOpening || chapter.isClosing,
      statusLabel: chapterStatusLabels[chapter.status],
      title: chapter.title,
    }));
}

export function buildBookAssemblyOrderedItems(
  source: BookAssemblySourceView,
  draft: Pick<BookAssemblyDraft, "selectedChapterIds" | "sideStoryPlacements">,
) {
  return createOrderedItemsFromSelections({
    chapters: source.chapters,
    placements: draft.sideStoryPlacements,
    selectedChapterIds: draft.selectedChapterIds,
    sideStories: source.sideStories,
  });
}

export function buildPlacementChoices(
  source: BookAssemblySourceView,
  draft: BookAssemblyDraft,
): SideStoryPlacementChoice[] {
  const byId = new Map(draft.sideStoryPlacements.map((placement) => [placement.storyId, placement]));

  return source.sideStories.map((story) => byId.get(story.id) ?? {
    anchorChapterId: source.chapters.at(-1)?.id ?? null,
    included: false,
    placementKind: "after-chapter",
    storyId: story.id,
    summary: story.summary,
    title: story.title,
  });
}

export function applySuggestionSetToDraft(
  source: BookAssemblySourceView,
  draft: BookAssemblyDraft,
  suggestionSet: SideStoryPlacementSuggestionSet,
) {
  const suggestionsByStoryId = new Map(
    suggestionSet.suggestions.map((suggestion) => [suggestion.storyId, suggestion]),
  );

  const sideStoryPlacements = draft.sideStoryPlacements.map((placement) => {
    const suggestion = suggestionsByStoryId.get(placement.storyId);
    if (!suggestion) {
      return placement;
    }

    return {
      ...placement,
      anchorChapterId: suggestion.anchorChapterId,
      included: true,
      placementKind: suggestion.suggestedPlacementKind,
    };
  });

  return {
    ...draft,
    orderedItems: createOrderedItemsFromSelections({
      chapters: source.chapters,
      placements: sideStoryPlacements,
      selectedChapterIds: draft.selectedChapterIds,
      sideStories: source.sideStories,
    }),
    sideStoryPlacements,
    suggestionSet,
  };
}

export function buildBookAssemblyLaunchState(source: BookAssemblySourceView) {
  return {
    availability: source.availability,
    chapterCount: source.chapters.length,
    href: `/projects/${source.projectId}/stories/book-assembly`,
    latestJobStatus: source.jobs?.[0]?.status ?? null,
    recommendedUpgradeTier: source.capabilities.recommendedUpgradeTier,
    sideStoryCount: source.sideStories.length,
    tier: source.tier,
  };
}

export function buildBookExportManifestSource(
  source: Pick<
    BookAssemblySourceView,
    "bookMetadata" | "chapters" | "exportIdentity" | "projectId" | "projectTitle" | "sideStories"
  >,
  settings?: Pick<
    BookAssemblyDraft["settings"],
    | "authorName"
    | "authorNote"
    | "bookSubtitle"
    | "bookTitle"
    | "coverAssetPath"
    | "dedication"
    | "seriesName"
    | "specialThanks"
    | "volumeLabel"
  >,
) {
  const projectIdentity = source.exportIdentity ?? buildProjectIdentity({
    projectId: source.projectId,
    projectTitle: source.projectTitle,
  });

  return {
    bookMetadata: {
      author_note_html: convertPlainTextToHtml(settings?.authorNote)
        ?? source.bookMetadata.author_note_html,
      dedication_html: convertPlainTextToHtml(settings?.dedication)
        ?? source.bookMetadata.dedication_html,
      special_thanks_html: convertPlainTextToHtml(settings?.specialThanks)
        ?? source.bookMetadata.special_thanks_html,
    },
    characterAppendixEntries: [],
    loreAppendixEntries: [],
    mainStoryChapters: source.chapters.map((chapter, index) => ({
      content_html: convertRichTextToSimpleHtml(chapter.body),
      id: chapter.id,
      illustrations: normalizeOptionalPath(chapter.imagePath)
        ? [
            {
              alignment: "center" as const,
              asset_id: normalizeOptionalPath(chapter.imagePath)!,
              caption: null,
              role: "chapter_opener" as const,
              size_mode: "wide" as const,
            },
          ]
        : [],
      order_index: index + 1,
      title: chapter.title,
    })),
    project: {
      ...projectIdentity,
      author_name: settings?.authorName?.trim() || projectIdentity.author_name,
      cover_image_path: normalizeOptionalPath(settings?.coverAssetPath)
        ?? projectIdentity.cover_image_path,
      series_name: settings?.seriesName?.trim() || projectIdentity.series_name,
      subtitle: settings?.bookSubtitle?.trim() || projectIdentity.subtitle,
      title: settings?.bookTitle?.trim() || projectIdentity.title,
      volume_label: settings?.volumeLabel?.trim() || projectIdentity.volume_label,
    },
    sideStories: source.sideStories.map((story, index) => ({
      content_html: convertRichTextToSimpleHtml(story.body),
      id: story.id,
      order_index: index + 1,
      title: story.title,
    })),
    timelineAppendixEntries: [],
  } satisfies BookExportManifestSource;
}

export function buildBookExportConfigFromDraft(
  source: Pick<BookAssemblySourceView, "capabilities" | "projectId" | "sideStories">,
  draft: Pick<
    BookAssemblyDraft,
    "exportConfig" | "selectedChapterIds" | "settings" | "sideStoryPlacements"
  >,
): BookExportConfig {
  const selectedSideStoryIds = collectIncludedSideStoryIds(draft.sideStoryPlacements).filter(
    (storyId) => source.sideStories.some((story) => story.id === storyId),
  );
  const requestedConfig = createDefaultBookExportConfig({
    ...source.capabilities.defaultConfig,
    ...draft.exportConfig,
    endMatterMessageSource: draft.exportConfig?.endMatterMessageSource
      ?? (draft.settings.specialThanks.trim().length > 0 ? "special_thanks" : "none"),
    exportMode: draft.exportConfig?.exportMode
      ?? inferBookExportModeFromSelections({
        placements: draft.sideStoryPlacements,
        selectedChapterIds: draft.selectedChapterIds,
      }),
    frontMatterMessageSource: draft.exportConfig?.frontMatterMessageSource
      ?? (draft.settings.authorNote.trim().length > 0
        ? "author_note"
        : draft.settings.dedication.trim().length > 0
          ? "dedication"
          : "none"),
    includeIllustrations: draft.exportConfig?.includeIllustrations
      ?? source.capabilities.defaultConfig.includeIllustrations,
    preset: draft.exportConfig?.preset ?? source.capabilities.defaultConfig.preset,
    selectedSideStoryIds,
    target: draft.exportConfig?.target ?? source.capabilities.defaultConfig.target,
  });

  return normalizeBookExportConfigForSource({
    availableSideStoryIds: source.sideStories.map((story) => story.id),
    capabilities: source.capabilities,
    config: requestedConfig,
    hasMainStory: draft.selectedChapterIds.length > 0,
  });
}

export function buildBookExportSummaryFromDraft(
  source: Pick<
    BookAssemblySourceView,
    "bookMetadata" | "capabilities" | "chapters" | "exportIdentity" | "projectId" | "projectTitle" | "sideStories"
  >,
  draft: Pick<
    BookAssemblyDraft,
    "exportConfig" | "selectedChapterIds" | "settings" | "sideStoryPlacements"
  >,
) {
  return buildBookExportSummary({
    config: buildBookExportConfigFromDraft(source, draft),
    source: buildBookExportManifestSource(source, draft.settings),
  });
}

export function buildStartBookExportPayload(
  source: Pick<
    BookAssemblySourceView,
    "capabilities" | "projectId" | "sideStories"
  >,
  draft: Pick<
    BookAssemblyDraft,
    "exportConfig" | "selectedChapterIds" | "settings" | "sideStoryPlacements"
  >,
) {
  const projectOverrides: BookExportProjectOverrides = {
    authorName: normalizeOptionalText(draft.settings.authorName),
    bookSubtitle: normalizeOptionalText(draft.settings.bookSubtitle),
    bookTitle: normalizeOptionalText(draft.settings.bookTitle),
    coverAssetPath: normalizeOptionalPath(draft.settings.coverAssetPath),
    seriesName: normalizeOptionalText(draft.settings.seriesName),
    volumeLabel: normalizeOptionalText(draft.settings.volumeLabel),
  };

  return {
    action: "start_export" as const,
    config: buildBookExportConfigFromDraft(source, draft),
    projectId: source.projectId,
    projectOverrides,
  };
}

export function buildPlacementSuggestionPayload(draft: BookAssemblyDraft) {
  return {
    includedSideStoryIds: draft.sideStoryPlacements
      .filter((placement) => placement.included)
      .map((placement) => placement.storyId),
    projectId: draft.projectId,
    selectedChapterIds: draft.selectedChapterIds,
  };
}

export function replaceOrderedItems(
  draft: BookAssemblyDraft,
  orderedItems: OrderedAssemblyItem[],
) {
  return {
    ...draft,
    orderedItems,
  };
}
